import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import ApiService from '../../services/api';
import SubExerciseList from './SubExerciseList';
import API_CONFIG from '../../config/api';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  IconButton,
  TextField,
  Typography,
  Container,
  Grid,
  Paper,
  Box,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Tab,
  Tabs,
  FormHelperText,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import ReorderIcon from '@mui/icons-material/Reorder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SubExerciseForm from './SubExerciseForm';

const AdminExercises = () => {
  const { token } = useContext(AuthContext);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [reorderMode, setReorderMode] = useState(false);
  const [subExercises, setSubExercises] = useState([]);
  const [orderChanged, setOrderChanged] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkUploadForm, setShowBulkUploadForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [bulkUploadData, setBulkUploadData] = useState('');
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [showSubExercises, setShowSubExercises] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [exerciseToDelete, setExerciseToDelete] = useState(null);
  const [newExercise, setNewExercise] = useState({
    name: '',
    description: '',
    category: '',
    difficulty: 'beginner',
    muscles: [],
    equipment: ''
  });
  
  const [uploadExercise, setUploadExercise] = useState({
    name: '',
    description: '',
    category: '',
    difficulty: 'Intermediate',
    muscles: [],
    equipment: 'None',
    instructions: '',
    videoUrl: ''
  });
  
  const [exerciseImage, setExerciseImage] = useState(null);
  const [editExerciseImage, setEditExerciseImage] = useState(null);
  const [editExerciseImageGirl, setEditExerciseImageGirl] = useState(null);
  const [exerciseImageGirl, setExerciseImageGirl] = useState(null);


  const handleEditImageChange = (event) => {
    const file = event.target.files[0];
    if (file && file.size <= 10 * 1024 * 1024) { // 10 MB limit
      setEditExerciseImage(file);
    } else {
      alert("File is too large. Maximum size is 10MB.");
    }
  };
  const handleImageChangeGirl = (e) => {
    const file = e.target.files[0];
    if (file) {
      setExerciseImageGirl(file);
    }
  };
  
  useEffect(() => {
    fetchExercises();
  }, [token, currentPage, searchTerm]);
  
  // This will ensure we save any changes when component unmounts
  useEffect(() => {
    return () => {
      if (reorderMode && orderChanged) {
        // Save changes if user navigates away
        handleSaveOrder();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      
      // Build query string
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 10
      });
      
      if (searchTerm) {
        queryParams.append('search', searchTerm);
      }
      
      // Fetch exercises
      const response = await ApiService.get(`/api/exercises?${queryParams.toString()}`, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        setExercises(data.exercises || []);
        setTotalPages(data?.pagination?.totalPages || 1);
      } else {
        toast.error(data.message || 'Failed to fetch exercises');
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  // Move exercise up (decrease order value)
  const handleMoveUp = (index) => {
    if (index <= 0) return; // Already at the top
    
    const items = Array.from(exercises);
    const temp = items[index];
    items[index] = items[index - 1];
    items[index - 1] = temp;
    
    // Update the order field of each exercise based on its new position
    const updatedExercises = items.map((item, idx) => ({
      ...item,
      order: idx
    }));
    
    setExercises(updatedExercises);
    setOrderChanged(true);
  };
  
  // Move exercise down (increase order value)
  const handleMoveDown = (index) => {
    if (index >= exercises.length - 1) return; // Already at the bottom
    
    const items = Array.from(exercises);
    const temp = items[index];
    items[index] = items[index + 1];
    items[index + 1] = temp;
    
    // Update the order field of each exercise based on its new position
    const updatedExercises = items.map((item, idx) => ({
      ...item,
      order: idx
    }));
    
    setExercises(updatedExercises);
    setOrderChanged(true);
  };
  
  // Save the new exercise order to the backend
  const handleSaveOrder = async () => {
    try {
      setLoading(true);
      
      // Update the order field based on current index
      const updatedExercises = exercises.map((exercise, index) => ({
        ...exercise,
        order: index
      }));
      setExercises(updatedExercises);
      
      // Prepare data for the API
      const orderData = updatedExercises.map((exercise) => ({
        _id: String(exercise._id),
        order: exercise.order
      }));
      
      // Send order update to the API
      const response = await ApiService.put('/api/exercises/update-order', { exercises: orderData }, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Exercise order updated successfully');
        setOrderChanged(false);
      } else {
        toast.error(data.message || 'Failed to update exercise order');
      }
    } catch (error) {
      console.error('Error updating exercise order:', error);
      toast.error('Error updating exercise order');
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle reorder mode
  const toggleReorderMode = () => {
    setReorderMode(!reorderMode);
    if (reorderMode && orderChanged) {
      // If exiting reorder mode and changes were made, ask if user wants to save
      if (window.confirm('Save the new exercise order?')) {
        handleSaveOrder();
      } else {
        // If user cancels, reload exercises to revert changes
        fetchExercises();
        setOrderChanged(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewExercise({ ...newExercise, [name]: value });
  };
  
  const handleUploadInputChange = (e) => {
    const { name, value } = e.target;
    setUploadExercise({ ...uploadExercise, [name]: value });
  };
  
  const handleImageChange = (e) => {
    setExerciseImage(e.target.files[0]);
  };

  const handleMuscleChange = (e) => {
    const selectedMuscles = Array.from(e.target.selectedOptions, option => option.value);
    setNewExercise({ ...newExercise, muscles: selectedMuscles });
  };
  
  const handleUploadMuscleChange = (e) => {
    // Material UI's Select component passes the selected values directly
    const selectedMuscles = e.target.value;
    setUploadExercise({ ...uploadExercise, muscles: selectedMuscles });
  };

  const handleAddExercise = async (e) => {
    e.preventDefault();
    
    if (!newExercise.name || !newExercise.category) {
      toast.error('Please enter exercise name and category');
      return;
    }
    
    try {
      const response = await ApiService.post('/api/admin/exercises', newExercise, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Exercise added successfully');
        setNewExercise({
          name: '',
          description: '',
          category: '',
          difficulty: 'beginner',
          muscles: [],
          equipment: ''
        });
        setShowAddForm(false);
        fetchExercises();
      } else {
        toast.error(data.message || 'Failed to add exercise');
      }
    } catch (error) {
      console.error('Error adding exercise:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    
    if (!bulkUploadData.trim()) {
      toast.error('Please enter valid JSON data');
      return;
    }
    
    try {
      // Parse JSON data
      const exercises = JSON.parse(bulkUploadData);
      
      if (!Array.isArray(exercises)) {
        toast.error('Data must be an array of exercises');
        return;
      }
      
      const response = await ApiService.post('/api/admin/exercises/bulk', { exercises }, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Successfully uploaded ${data.count} exercises`);
        setBulkUploadData('');
        setShowBulkUploadForm(false);
        fetchExercises();
      } else {
        toast.error(data.message || 'Failed to upload exercises');
      }
    } catch (error) {
      console.error('Error uploading exercises:', error);
      
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format. Please check your data.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    }
  };
  
  // Handle exercise upload with image
  const handleUploadExercise = async (e) => {
    e.preventDefault();
    
    if (!uploadExercise.name || !uploadExercise.description || !uploadExercise.category) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      // Create form data for multipart/form-data request
      const formData = new FormData();
      formData.append('name', uploadExercise.name);
      formData.append('description', uploadExercise.description);
      formData.append('category', uploadExercise.category);
      formData.append('difficulty', uploadExercise.difficulty);
      
      if (uploadExercise.muscles.length > 0) {
        formData.append('muscles', JSON.stringify(uploadExercise.muscles));
      }
       
        formData.append('equipment', uploadExercise.equipment);
      
      if (uploadExercise.instructions) {
        formData.append('instructions', uploadExercise.instructions);
      }
      
      if (uploadExercise.videoUrl) {
        formData.append('videoUrl', uploadExercise.videoUrl);
      }

      if(exerciseImage ) {
        formData.append('exerciseImage', exerciseImage);
      }
      
      if(exerciseImageGirl) {
        formData.append('exerciseImageGirl', exerciseImageGirl);
      }
      
      const response = await ApiService.fetchApi('/api/admin/exercises/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message || 'Exercise uploaded successfully');
        setUploadExercise({
          name: '',
          description: '',
          category: '',
          difficulty: 'Intermediate',
          muscles: [],
          equipment: 'None',
          instructions: '',
          videoUrl: '',
          exerciseImage: null,
          exerciseImageGirl: null
        });
        setExerciseImage(null);
        setExerciseImageGirl(null);
        setShowUploadForm(false);
        fetchExercises();
      } else {
        toast.error(data.message || 'Failed to upload exercise');
      }
    } catch (error) {
      console.error('Error uploading exercise:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleEditExercise = (exercise) => {
    setEditingExercise({
      ...exercise,
      muscles: Array.isArray(exercise.muscles) ? exercise.muscles : []
    });
    setShowEditForm(true);
    setShowAddForm(false);
    setShowBulkUploadForm(false);
    setShowUploadForm(false);
  };

  const handleUpdateExercise = async (e) => {
    e.preventDefault();
  
    if (!editingExercise?.name || !editingExercise?.category) {
      toast.error('Please enter exercise name and category');
      return;
    }
  
    try {
      setLoading(true);
  
      const formData = new FormData();
      formData.append('name', editingExercise.name);
      formData.append('description', editingExercise.description || '');
      formData.append('category', editingExercise.category);
      formData.append('difficulty', editingExercise.difficulty || '');
      formData.append('equipment', editingExercise.equipment || '');
      formData.append('instructions', editingExercise.instructions || '');
      formData.append('videoUrl', editingExercise.videoUrl || '');
      formData.append('muscles', JSON.stringify(editingExercise.muscles || []));
  
      // ✅ Append image only if a new one is selected
      if (editExerciseImage) {
        formData.append('exerciseImage', editExerciseImage);
      }
      
      if(editExerciseImageGirl ) {
        formData.append('exerciseImageGirl', editExerciseImageGirl);
      }
  
      const response = await ApiService.fetchApi(`/api/admin/exercises/${editingExercise._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
          // ⛔ Don't set 'Content-Type'
        },
        body: formData
      });
  
      const data = await response.json();
  
      if (data.success) {
        toast.success('Exercise updated successfully');
        setShowEditForm(false);
        setEditingExercise(null);
        setEditExerciseImage(null);
        fetchExercises();
      } else {
        toast.error(data.message || 'Failed to update exercise');
      }
    } catch (error) {
      console.error('Error updating exercise:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingExercise({ ...editingExercise, [name]: value });
  };

  const handleEditMuscleChange = (e) => {
    // Material UI's Select component passes the selected values directly
    const selectedMuscles = e.target.value;
    setEditingExercise({ ...editingExercise, muscles: selectedMuscles });
  };

  const handleEditImageChangeGirl = (e) => {
    setEditExerciseImageGirl(e.target.files[0]);
  };

  const handleDeleteClick = (exercise) => {
    setExerciseToDelete(exercise);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteExercise = async () => {
    if (!exerciseToDelete?._id) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await ApiService.delete(`/api/admin/exercises/${exerciseToDelete._id}`, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Exercise deleted successfully');
        fetchExercises();
      } else {
        toast.error(data.message || 'Failed to delete exercise');
      }
    } catch (error) {
      console.error('Error deleting exercise:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setExerciseToDelete(null);
    }
  };

  const handleManageSubExercises = (exercise) => {
    console.log(exercise);
    setSelectedExercise(exercise);
    setShowSubExercises(true);
  };


    useEffect(() => {
      if (selectedExercise) {
        fetchSubExercises();
      }
    }, [selectedExercise, token]);
  
    const fetchSubExercises = async () => {
      if (!selectedExercise) return;
      
      try {
        setLoading(true);
        const response = await ApiService.get(`/api/admin/exercises/${selectedExercise._id}/sub-exercises`, {
          'Authorization': `Bearer ${token}`
        });
        
        const data = await response.json();
        
        if (data.success) {
          // Sort by order field
          const sortedSubExercises = [...data.subExercises].sort((a, b) => a.order - b.order);
          setSubExercises(sortedSubExercises);
        } else {
          toast.error(data.message || 'Failed to fetch sub-exercises');
        }
      } catch (error) {
        console.error('Error fetching sub-exercises:', error);
        toast.error('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    };
  
    const handleSubExerciseAdded = (newSubExercise) => {
      fetchSubExercises();
    };
  
  const handleCloseSubExercises = () => {
    setShowSubExercises(false);
    setSelectedExercise(null);
  };

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  if (loading && exercises.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography variant="h6">Loading exercises...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            <FitnessCenterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Manage Exercises
          </Typography>
        </Box>
        
        {/* Search and Action Buttons */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <form onSubmit={handleSearchSubmit}>
              <TextField
                fullWidth
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search exercises..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button 
                        type="submit" 
                        variant="contained" 
                        color="primary" 
                        size="small"
                      >
                        Search
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </form>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant={reorderMode ? "contained" : "outlined"}
                color="secondary"
                startIcon={<ReorderIcon />}
                onClick={toggleReorderMode}
                disabled={loading}
              >
                {reorderMode ? "Exit Reorder Mode" : "Reorder Exercises"}
              </Button>
              
              {orderChanged && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveOrder}
                  disabled={loading}
                >
                  Save Order
                </Button>
              )}
              
              <Button
                variant={showAddForm ? "outlined" : "contained"}
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setShowAddForm(!showAddForm);
                  setShowBulkUploadForm(false);
                  setShowUploadForm(false);
                }}
                disabled={reorderMode}
              >
                {showAddForm ? 'Cancel' : 'Add Exercise'}
              </Button>
              
              <Button
                variant={showBulkUploadForm ? "outlined" : "contained"}
                color="secondary"
                startIcon={<CloudUploadIcon />}
                onClick={() => {
                  setShowBulkUploadForm(!showBulkUploadForm);
                  setShowAddForm(false);
                  setShowUploadForm(false);
                }}
              >
                {showBulkUploadForm ? 'Cancel' : 'Bulk Upload'}
              </Button>
              
              <Button
                variant={showUploadForm ? "outlined" : "contained"}
                color="success"
                startIcon={<CloudUploadIcon />}
                onClick={() => {
                  setShowUploadForm(!showUploadForm);
                  setShowAddForm(false);
                  setShowBulkUploadForm(false);
                }}
              >
                {showUploadForm ? 'Cancel' : 'Upload Exercise'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      
      {/* Add Exercise Form */}
      {showAddForm && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Add Official Exercise
          </Typography>
          <form onSubmit={handleAddExercise}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  value={newExercise.name}
                  onChange={(e) => setNewExercise({ ...newExercise, name: e.target.value })}
                  required
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newExercise.category}
                    onChange={(e) => setNewExercise({ ...newExercise, category: e.target.value })}
                    label="Category"
                    required
                  >
                    <MenuItem value=""><em>Select Category</em></MenuItem>
                    <MenuItem value="Strength">Strength</MenuItem>
                    <MenuItem value="Cardio">Cardio</MenuItem>
                    <MenuItem value="Flexibility">Flexibility</MenuItem>
                    <MenuItem value="Balance">Balance</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  variant="outlined"
                  value={newExercise.description}
                  onChange={(e) => setNewExercise({ ...newExercise, description: e.target.value })}
                  required
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    value={newExercise.difficulty}
                    onChange={(e) => setNewExercise({ ...newExercise, difficulty: e.target.value })}
                    label="Difficulty"
                    required
                  >
                    <MenuItem value="Beginner">Beginner</MenuItem>
                    <MenuItem value="Intermediate">Intermediate</MenuItem>
                    <MenuItem value="Advanced">Advanced</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Equipment"
                  variant="outlined"
                  value={newExercise.equipment}
                  onChange={(e) => setNewExercise({ ...newExercise, equipment: e.target.value })}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Muscles (comma separated)"
                  variant="outlined"
                  value={newExercise.muscles.join(', ')}
                  onChange={(e) => setNewExercise({ 
                    ...newExercise, 
                    muscles: e.target.value.split(',').map(m => m.trim()).filter(m => m) 
                  })}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Instructions"
                  variant="outlined"
                  value={newExercise.instructions}
                  onChange={(e) => setNewExercise({ ...newExercise, instructions: e.target.value })}
                  multiline
                  rows={4}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Video URL"
                  variant="outlined"
                  type="url"
                  value={newExercise.videoUrl}
                  onChange={(e) => setNewExercise({ ...newExercise, videoUrl: e.target.value })}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box mt={2}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    startIcon={<SaveIcon />}
                  >
                    Add Exercise
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}
      
      {/* Upload Exercise Form with Image */}
      {showUploadForm && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Upload Exercise with Image
          </Typography>
          <form onSubmit={handleUploadExercise}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Exercise Name"
                  variant="outlined"
                  name="name"
                  value={uploadExercise.name}
                  onChange={handleUploadInputChange}
                  required
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={uploadExercise.category}
                    onChange={handleUploadInputChange}
                    label="Category"
                    required
                  >
                    <MenuItem value=""><em>Select Category</em></MenuItem>
                    <MenuItem value="Chest">Chest</MenuItem>
                    <MenuItem value="Back">Back</MenuItem>
                    <MenuItem value="Legs">Legs</MenuItem>
                    <MenuItem value="Shoulders">Shoulders</MenuItem>
                    <MenuItem value="Arms">Arms</MenuItem>
                    <MenuItem value="Core">Core</MenuItem>
                    <MenuItem value="Cardio">Cardio</MenuItem>
                    <MenuItem value="Full Body">Full Body</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  variant="outlined"
                  name="description"
                  value={uploadExercise.description}
                  onChange={handleUploadInputChange}
                  required
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    name="difficulty"
                    value={uploadExercise.difficulty}
                    onChange={handleUploadInputChange}
                    label="Difficulty"
                  >
                    <MenuItem value="Beginner">Beginner</MenuItem>
                    <MenuItem value="Intermediate">Intermediate</MenuItem>
                    <MenuItem value="Advanced">Advanced</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Equipment</InputLabel>
                  <Select
                    name="equipment"
                    value={uploadExercise.equipment}
                    onChange={handleUploadInputChange}
                    label="Equipment"
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="Dumbbells">Dumbbells</MenuItem>
                    <MenuItem value="Barbell">Barbell</MenuItem>
                    <MenuItem value="Machine">Machine</MenuItem>
                    <MenuItem value="Cables">Cables</MenuItem>
                    <MenuItem value="Bodyweight">Bodyweight</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Target Muscles</InputLabel>
                  <Select
                    multiple
                    name="muscles"
                    value={uploadExercise.muscles}
                    onChange={handleUploadMuscleChange}
                    input={<OutlinedInput label="Target Muscles" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Chest">Chest</MenuItem>
                    <MenuItem value="Back">Back</MenuItem>
                    <MenuItem value="Quadriceps">Quadriceps</MenuItem>
                    <MenuItem value="Hamstrings">Hamstrings</MenuItem>
                    <MenuItem value="Calf">Calf</MenuItem>
                    <MenuItem value="Shoulders">Shoulders</MenuItem>
                    <MenuItem value="Biceps">Biceps</MenuItem>
                    <MenuItem value="Triceps">Triceps</MenuItem>
                    <MenuItem value="Forearms">Forearms</MenuItem>
                    <MenuItem value="Abs">Abs</MenuItem>
                    <MenuItem value="Obliques">Obliques</MenuItem>
                    <MenuItem value="Lower Back">Lower Back</MenuItem>
                    <MenuItem value="Glutes">Glutes</MenuItem>
                    <MenuItem value="Legs">Legs</MenuItem>
                  </Select>
                  <FormHelperText>Hold Ctrl/Cmd to select multiple</FormHelperText>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Instructions"
                  variant="outlined"
                  name="instructions"
                  value={uploadExercise.instructions}
                  onChange={handleUploadInputChange}
                  multiline
                  rows={4}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Video URL"
                  variant="outlined"
                  name="videoUrl"
                  value={uploadExercise.videoUrl}
                  onChange={handleUploadInputChange}
                  placeholder="e.g. https://youtube.com/watch?v=..."
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 1 }}
                  >
                    Upload Image
                    <VisuallyHiddenInput 
                      type="file" 
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                  {exerciseImage && (
                    <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                      File selected: {exerciseImage.name}
                    </Typography>
                  )}
                  <FormHelperText>Upload an image showing the exercise (max 10MB)</FormHelperText>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 1 }}
                  >
                    Upload Girl Image
                    <VisuallyHiddenInput 
                      type="file" 
                      accept="image/*"
                      onChange={handleImageChangeGirl}
                    />
                  </Button>
                  {exerciseImageGirl && (
                    <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                      File selected: {exerciseImageGirl.name}
                    </Typography>
                  )}
                  <FormHelperText>Upload a girl version image of the exercise (max 10MB)</FormHelperText>
                </Box>
              </Grid>

              
              <Grid item xs={12}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="success"
                  startIcon={<CloudUploadIcon />}
                  size="large"
                >
                  Upload Exercise
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}
      
      {/* Bulk Upload Form */}
      {showBulkUploadForm && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Bulk Upload Exercises
          </Typography>
          <form onSubmit={handleBulkUpload}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="JSON Data"
                  variant="outlined"
                  multiline
                  rows={10}
                  value={bulkUploadData}
                  onChange={(e) => setBulkUploadData(e.target.value)}
                  placeholder='[{"name": "Bench Press", "category": "Chest", "difficulty": "intermediate", "muscles": ["chest", "triceps"], "equipment": "barbell"}]'
                  required
                  margin="normal"
                  helperText="Enter exercises as a JSON array"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="success"
                  startIcon={<CloudUploadIcon />}
                  size="large"
                >
                  Upload Exercises
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}
      
      {/* Exercise Table */}
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              {reorderMode && <TableCell width="50px"></TableCell>}
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Difficulty</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          
          {reorderMode ? (
            <TableBody>
              {exercises.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">No exercises found</TableCell>
                </TableRow>
              ) : (
                exercises.map((exercise, index) => (
                  <TableRow
                    key={exercise._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          sx={{ mb: 1 }}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleMoveDown(index)}
                          disabled={index === exercises.length - 1}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>{exercise.name}</TableCell>
                    <TableCell>{exercise.category}</TableCell>
                    <TableCell>{exercise.difficulty}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="textSecondary">
                        Order: {exercise.order || index}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          ) : (
            <TableBody>
              {exercises.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">No exercises found</TableCell>
                </TableRow>
              ) : (
                exercises.map(exercise => (
                  <TableRow key={exercise._id}>
                    <TableCell>{exercise.name}</TableCell>
                    <TableCell>{exercise.category}</TableCell>
                    <TableCell>{exercise.difficulty}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<EditIcon />}
                        sx={{ mr: 1 }}
                        onClick={() => handleManageSubExercises(exercise)}
                      >
                        Sub-Exercises
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditExercise(exercise)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteClick(exercise)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" my={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => handlePageChange(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Sub-Exercises Dialog */}
      {/* <Dialog
        // open={showSubExercises}
        // onClose={handleCloseSubExercises}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Sub-Exercises for {selectedExercise?.name}
        </DialogTitle>
        <DialogContent> */}
          {/* {selectedExercise && (
            <SubExerciseList 
              exerciseId={selectedExercise._id} 
              token={token} 
            />
          )} */}
            {selectedExercise && (
        <SubExerciseForm
          open={showSubExercises}
          onClose={handleCloseSubExercises}
          handleClose={handleCloseSubExercises}
          exerciseId={selectedExercise._id}
          token={token}
          onSubExerciseAdded={handleSubExerciseAdded}
        />
      )}
        {/* </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubExercises} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog> */}

      {/* Edit Exercise Form */}
      {showEditForm && editingExercise && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Edit Exercise: {editingExercise.name}
          </Typography>
          <form onSubmit={handleUpdateExercise}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  name="name"
                  value={editingExercise.name || ''}
                  onChange={handleEditInputChange}
                  required
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={editingExercise.category || ''}
                    onChange={handleEditInputChange}
                    label="Category"
                    required
                  >
                    <MenuItem value=""><em>Select Category</em></MenuItem>
                    <MenuItem value="Strength">Strength</MenuItem>
                    <MenuItem value="Cardio">Cardio</MenuItem>
                    <MenuItem value="Flexibility">Flexibility</MenuItem>
                    <MenuItem value="Balance">Balance</MenuItem>
                    <MenuItem value="Chest">Chest</MenuItem>
                    <MenuItem value="Back">Back</MenuItem>
                    <MenuItem value="Legs">Legs</MenuItem>
                    <MenuItem value="Shoulders">Shoulders</MenuItem>
                    <MenuItem value="Arms">Arms</MenuItem>
                    <MenuItem value="Core">Core</MenuItem>
                    <MenuItem value="Full Body">Full Body</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  variant="outlined"
                  name="description"
                  value={editingExercise.description || ''}
                  onChange={handleEditInputChange}
                  required
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    name="difficulty"
                    value={editingExercise.difficulty || 'Intermediate'}
                    onChange={handleEditInputChange}
                    label="Difficulty"
                    required
                  >
                    <MenuItem value="Beginner">Beginner</MenuItem>
                    <MenuItem value="Intermediate">Intermediate</MenuItem>
                    <MenuItem value="Advanced">Advanced</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Equipment"
                  variant="outlined"
                  name="equipment"
                  value={editingExercise.equipment || ''}
                  onChange={handleEditInputChange}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Target Muscles</InputLabel>
                  <Select
                    multiple
                    name="muscles"
                    value={editingExercise.muscles || []}
                    onChange={handleEditMuscleChange}
                    input={<OutlinedInput label="Target Muscles" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Chest">Chest</MenuItem>
                    <MenuItem value="Back">Back</MenuItem>
                    <MenuItem value="Quadriceps">Quadriceps</MenuItem>
                    <MenuItem value="Hamstrings">Hamstrings</MenuItem>
                    <MenuItem value="Calf">Calf</MenuItem>
                    <MenuItem value="Shoulders">Shoulders</MenuItem>
                    <MenuItem value="Biceps">Biceps</MenuItem>
                    <MenuItem value="Triceps">Triceps</MenuItem>
                    <MenuItem value="Forearms">Forearms</MenuItem>
                    <MenuItem value="Abs">Abs</MenuItem>
                    <MenuItem value="Obliques">Obliques</MenuItem>
                    <MenuItem value="Lower Back">Lower Back</MenuItem>
                    <MenuItem value="Glutes">Glutes</MenuItem>
                    <MenuItem value="Legs">Legs</MenuItem>
                  </Select>
                  <FormHelperText>Hold Ctrl/Cmd to select multiple</FormHelperText>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Instructions"
                  variant="outlined"
                  name="instructions"
                  value={editingExercise.instructions || ''}
                  onChange={handleEditInputChange}
                  multiline
                  rows={4}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Video URL"
                  variant="outlined"
                  name="videoUrl"
                  value={editingExercise.videoUrl || ''}
                  onChange={handleEditInputChange}
                  margin="normal"
                />
              </Grid>
            {/* Image edit optin line current image then upload image option*/}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  variant="outlined"
                  name="imageUrl"
                  value={editingExercise.imageUrl || ''}
                  onChange={handleEditInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL Girl"
                  variant="outlined"
                  name="imageUrlGirl"
                  value={editingExercise.imageUrlGirl || ''}
                  onChange={handleEditImageChangeGirl}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 1 }}
                  >
                    Upload New Image
                    <VisuallyHiddenInput 
                      type="file" 
                      accept="image/*"
                      onChange={handleEditImageChange} // separate handler for edit
                    />
                  </Button>
                  {editExerciseImage && (
                    <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                      File selected: {editExerciseImage.name}
                    </Typography>
                  )}

                  {console.log("editExerciseImage",editExerciseImage)}
                {  console.log("editExerciseImagewith Base URL",API_CONFIG.baseURL + editingExercise.imageUrl)}
                  <img
                    src={ API_CONFIG.baseURL + editingExercise.imageUrl}
                    alt="Preview"
                    style={{ maxHeight: '150px', marginTop: '10px' }}
                  />
                  <FormHelperText>Replace existing image (max 10MB)</FormHelperText>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 1 }}
                  >
                    Upload New Image Girl
                    <VisuallyHiddenInput 
                      type="file" 
                      accept="image/*"
                      onChange={handleEditImageChangeGirl} // separate handler for edit
                    />
                  </Button>
                  {editExerciseImageGirl && (
                    <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                      File selected: {editExerciseImageGirl.name}
                    </Typography>
                  )}

                  {console.log("editExerciseImageGirl",editingExercise.imageUrlGirl)}
                  <img
                    src={ API_CONFIG.baseURL + editingExercise.imageUrlGirl}
                    alt="Preview"
                    style={{ maxHeight: '150px', marginTop: '10px' }}
                  />
                  <FormHelperText>Replace existing image (max 10MB)</FormHelperText>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Update Exercise'}
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      setShowEditForm(false);
                      setEditingExercise(null);
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the exercise "{exerciseToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={loading}>Cancel</Button>
          <Button 
            onClick={handleDeleteExercise} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
     
  </Container>
  );
};

export default AdminExercises;
